/**
 * Fees — the most consequential screen in the console.
 *
 * A rate is a DATED ROW. Changing one writes a new row, so what a tenant was charged last month stays
 * exactly what it was. Nothing here is editable, and nothing is deleted.
 *
 * The drawer shows the effect in naira BEFORE saving, because "1.5% → 1.0%" is not a number anyone can
 * feel, and a fee change is the one action here that silently costs or earns money on every future payment.
 */
import { useMemo, useState } from "react";
import { api, type FeeConfigRow } from "../lib/api";
import { useLoader, useSession } from "../lib/session";
import { bpsToPercent, dateLong, money } from "../lib/format";
import { ActionButton, C, card, CardHead, Drawer, EmptyState, ErrorState, input, label, labelText, Skeleton } from "../ui/primitives";

const RAILS = ["virtual_account", "card_checkout", "pos", "payout"] as const;
const RAIL_LABEL: Record<string, string> = {
  virtual_account: "Bank transfer (virtual account)",
  card_checkout: "Card",
  pos: "POS terminal",
  payout: "Payout",
};

/** The fee an amount would attract under a given rate — the same arithmetic the engine uses. */
function computeFee(amountNaira: number, percentBps: number, flatKobo: number, capKobo: number | null): number {
  const raw = amountNaira * (percentBps / 10_000) + flatKobo / 100;
  const capped = capKobo !== null ? Math.min(raw, capKobo / 100) : raw;
  return Math.round(Math.max(0, capped) * 100) / 100;
}

export function Fees() {
  const { locked } = useSession();
  const { data, error, loading, reload } = useLoader(() => api.fees(), []);
  const [open, setOpen] = useState(false);

  const rows = data ?? [];
  // Effective TODAY = the newest row per (scope, rail) whose effectiveFrom has passed.
  const effective = useMemo(() => {
    const now = Date.now();
    const best = new Map<string, FeeConfigRow>();
    for (const r of rows) {
      if (new Date(r.effectiveFrom).getTime() > now) continue;
      const key = `${r.tenantId ?? "global"}:${r.rail}`;
      const prev = best.get(key);
      if (!prev || new Date(r.effectiveFrom) > new Date(prev.effectiveFrom)) best.set(key, r);
    }
    return [...best.values()].sort((a, b) => (a.tenantId ?? "").localeCompare(b.tenantId ?? "") || a.rail.localeCompare(b.rail));
  }, [rows]);

  if (loading) return <div style={card}><Skeleton label="Loading rates…" /></div>;
  if (error) return <div style={card}><ErrorState title="Couldn't load fees." body={error} onRetry={reload} /></div>;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 12, alignItems: "start" }}>
        <div style={card}>
          <CardHead title="Effective rates today"
            sub="A rate is a dated row. Changing one writes a new row — what a tenant was charged last month stays exactly what it was."
            right={<ActionButton small locked={locked} onClick={() => setOpen(true)}>Change a rate</ActionButton>} />
          {effective.length === 0 ? (
            <EmptyState title="No rates configured."
              body="Nothing is charged until a rate exists. That is deliberate: an unset fee is ZERO, never a guessed default — the engine will not invent a number nobody agreed." />
          ) : effective.map((r) => (
            <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1fr 90px 110px 1fr", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
              <div style={{ minWidth: 0 }}>
                <div className="bp-mono" style={{ fontSize: 12.5, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis" }}>{r.tenantId ?? "Global default"}</div>
                <div style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 700 }}>{r.tenantId ? "Per-tenant override" : "All tenants without an override"} · {RAIL_LABEL[r.rail] ?? r.rail}</div>
              </div>
              <span className="bp-mono" style={{ fontSize: 14, fontWeight: 700 }}>{bpsToPercent(r.percentBps)}</span>
              <span className="bp-mono" style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink2)" }}>
                {r.capKobo !== null ? `cap ${money(r.capKobo / 100)}` : r.flatKobo ? `+ ${money(r.flatKobo / 100)}` : "no cap"}
              </span>
              <span style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 600, textAlign: "right" }}>since {dateLong(r.effectiveFrom)}</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <CardHead title="Rate history" sub="Every change, when it took effect, and why. Nothing here is editable." />
          {rows.length === 0
            ? <EmptyState title="No history yet." body="The first rate you set will appear here, and every change after it." />
            : rows.map((h) => (
              <div key={h.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="bp-mono" style={{ fontSize: 12.5, fontWeight: 800 }}>{h.tenantId ?? "Global default"}</span>
                  <span className="bp-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--ink2)" }}>
                    {bpsToPercent(h.percentBps)}{h.capKobo !== null ? ` · cap ${money(h.capKobo / 100)}` : ""}
                  </span>
                  <span style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 600, marginLeft: "auto" }}>from {dateLong(h.effectiveFrom)}</span>
                </div>
                <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600 }}>
                  {RAIL_LABEL[h.rail] ?? h.rail}{h.reason ? ` — "${h.reason}"` : " — no reason recorded"}
                </div>
              </div>
            ))}
        </div>
      </div>

      {open && <FeeDrawer existing={effective} onClose={() => setOpen(false)} onDone={() => { setOpen(false); reload(); }} />}
    </>
  );
}

function FeeDrawer({ existing, onClose, onDone }: { existing: FeeConfigRow[]; onClose: () => void; onDone: () => void }) {
  const [tenantId, setTenantId] = useState("");
  const [rail, setRail] = useState<string>("virtual_account");
  const [percent, setPercent] = useState("1.5");
  const [cap, setCap] = useState("2000");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const bps = Math.round(parseFloat(percent || "0") * 100);
  const capKobo = cap.trim() === "" ? null : Math.round(parseFloat(cap) * 100);
  const current = existing.find((r) => (r.tenantId ?? "") === tenantId.trim() && r.rail === rail) ?? null;

  // The effect, in naira, on a payment an operator can picture.
  const SAMPLE = 10_000;
  const before = current ? computeFee(SAMPLE, current.percentBps, current.flatKobo, current.capKobo) : null;
  const after = Number.isFinite(bps) ? computeFee(SAMPLE, bps, 0, capKobo) : null;

  // Takes effect at the NEXT midnight — never mid-day, so no day is ever charged at two different rates.
  const effectiveFrom = new Date();
  effectiveFrom.setHours(24, 0, 0, 0);

  async function save() {
    if (!Number.isFinite(bps) || bps < 0 || bps > 10_000) { setErr("Enter a percentage between 0 and 100."); return; }
    if (reason.trim().length < 3) { setErr("A reason is required — this is the most consequential change in the console."); return; }
    setBusy(true); setErr(null);
    try {
      await api.setFee({
        tenantId: tenantId.trim() || null, rail, percentBps: bps, flatKobo: 0,
        capKobo, effectiveFrom: effectiveFrom.toISOString(), reason: reason.trim(),
      });
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not save"); setBusy(false); }
  }

  return (
    <Drawer title="Change a fee rate" onClose={onClose}>
      <label style={label}>
        <span style={labelText}>Scope</span>
        <input value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="Tenant id — leave blank for the global default" style={input} />
      </label>
      <label style={label}>
        <span style={labelText}>Rail</span>
        <select value={rail} onChange={(e) => setRail(e.target.value)} style={input}>
          {RAILS.map((r) => <option key={r} value={r}>{RAIL_LABEL[r]}</option>)}
        </select>
      </label>
      <div style={{ display: "flex", gap: 10 }}>
        <label style={{ ...label, flex: 1 }}><span style={labelText}>New rate (%)</span>
          <input value={percent} onChange={(e) => setPercent(e.target.value)} inputMode="decimal" className="bp-mono" style={input} /></label>
        <label style={{ ...label, flex: 1 }}><span style={labelText}>Cap per payment (₦)</span>
          <input value={cap} onChange={(e) => setCap(e.target.value)} inputMode="decimal" placeholder="blank = no cap" className="bp-mono" style={input} /></label>
      </div>

      <div style={{ padding: "13px 14px", background: "var(--accentsoft)", border: "1px solid var(--line2)", borderRadius: 12, display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "var(--muted)", letterSpacing: ".05em" }}>EFFECT BEFORE YOU SAVE</span>
        <span style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink)" }}>
          A <b className="bp-mono">{money(SAMPLE)}</b> payment{" "}
          {before === null
            ? <>is <b>not charged at all</b> today for this scope.</>
            : <>currently costs <b className="bp-mono">{money(before)}</b>.</>}
          <br />
          After this change it will cost <b className="bp-mono" style={{ color: C.accent }}>{after === null ? "—" : money(after)}</b>.
        </span>
        <span style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600 }}>
          Takes effect {dateLong(effectiveFrom)}, 00:00 WAT. Nothing already charged changes.
        </span>
      </div>

      <label style={label}>
        <span style={labelText}>Reason — required</span>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Why this rate is changing" style={{ ...input, lineHeight: 1.5, resize: "vertical" }} />
      </label>

      <div style={{ padding: "10px 13px", border: `1px solid ${C.warnLine}`, background: C.warnSoft, borderRadius: 11, fontSize: 12, lineHeight: 1.55, color: "var(--ink2)" }}>
        <b>This writes a new dated rate row under your name.</b> It appears as a flag for 14 days, and stays in this scope's history permanently. The old rate is not edited or removed.
      </div>

      {err && <div style={{ fontSize: 12, fontWeight: 700, color: C.danger }}>{err}</div>}
      <div style={{ display: "flex", gap: 9 }}>
        <ActionButton tone="quiet" onClick={onClose}>Cancel</ActionButton>
        <div style={{ flex: 1 }}>
          <button type="button" onClick={() => void save()} disabled={busy}
            style={{ width: "100%", padding: 12, border: "none", borderRadius: 11, background: busy ? "var(--muted)" : C.accent, color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Saving…" : `Save new rate from ${dateLong(effectiveFrom)}`}
          </button>
        </div>
      </div>
    </Drawer>
  );
}
